import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  HeartStraightIcon,
  CheckCircleIcon,
  XCircleIcon,
  BroadcastIcon,
} from "@phosphor-icons/react";

export default function TipSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [amount, setAmount] = useState(0);
  const [sellerName, setSellerName] = useState("");
  const [streamId, setStreamId] = useState(null);
  const [kind, setKind] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let alive = true;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/tips/status/${sessionId}`);
        if (!alive) return;
        setAmount(Number(data.amount || 0));
        setSellerName(data.seller_name || "");
        setStreamId(data.stream_id || null);
        setKind(data.kind || null);
        if (data.payment_status === "paid" && data.credited) {
          setStatus("success");
        } else if (data.status === "expired") {
          setStatus("expired");
        } else if (attempts >= 10) {
          setStatus("timeout");
        } else {
          setAttempts((a) => a + 1);
          timer = setTimeout(poll, 2000);
        }
      } catch (e) {
        if (!alive) return;
        toast.error(apiError(e));
        setStatus("error");
      }
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-16 text-center">
        {status === "polling" && (
          <>
            <div className="h-16 w-16 rounded-full bg-emerald-100 mx-auto grid place-items-center animate-pulse">
              <HeartStraightIcon size={28} className="text-emerald-600" weight="fill" />
            </div>
            <h1 className="font-display font-black text-3xl mt-4 tracking-tight">
              Confirming your tip…
            </h1>
            <p className="text-sm text-zinc-500 mt-2">
              Hang tight — Stripe usually takes a couple seconds.
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="h-20 w-20 rounded-full bg-emerald-500 mx-auto grid place-items-center">
              <CheckCircleIcon size={36} className="text-white" weight="fill" />
            </div>
            <h1 className="font-display font-black text-4xl mt-5 tracking-tighter">
              ${amount.toFixed(2)} sent{sellerName ? ` to ${sellerName}` : ""}.
            </h1>
            <p className="text-zinc-600 mt-3">
              They've been credited 100%. Thanks for supporting the community.
            </p>
            <div className="mt-8 flex gap-3 justify-center flex-wrap">
              {streamId && (
                <Button asChild className="bg-[#1D78AB] hover:bg-[#165d85] rounded-full">
                  <Link to={`/stream/${streamId}`} data-testid="tip-success-back-stream">
                    <BroadcastIcon size={14} weight="fill" className="mr-2" />
                    Back to the stream
                  </Link>
                </Button>
              )}
              {!streamId && kind === "seller_tip" && (
                <Button asChild className="bg-[#1D78AB] hover:bg-[#165d85] rounded-full">
                  <Link to="/dashboard?tab=library" data-testid="tip-success-back-library">
                    Back to your library
                  </Link>
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate("/live")}
                className="rounded-full"
                data-testid="tip-success-live-btn"
              >
                Browse live streams
              </Button>
            </div>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <div className="h-20 w-20 rounded-full bg-red-100 mx-auto grid place-items-center">
              <XCircleIcon size={36} className="text-red-600" weight="fill" />
            </div>
            <h1 className="font-display font-black text-3xl mt-5 tracking-tight">
              {status === "expired"
                ? "Checkout expired"
                : status === "timeout"
                ? "Still confirming…"
                : "We couldn't confirm the tip"}
            </h1>
            <p className="text-zinc-600 mt-3">
              {status === "timeout"
                ? `If Stripe charged you, your ${amount ? `$${amount.toFixed(2)} ` : ""}tip will land in the seller's payouts within a minute. Check back on the stream shortly — no need to pay again.`
                : "Your card was not charged if no tip was credited. Try again or reach out."}
            </p>
            <div className="mt-6 flex gap-3 justify-center flex-wrap">
              {streamId && status === "timeout" && (
                <Button asChild className="rounded-full bg-[#1D78AB] hover:bg-[#165d85]">
                  <Link to={`/stream/${streamId}`} data-testid="tip-timeout-back-stream">
                    Back to the stream
                  </Link>
                </Button>
              )}
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/live">Back to live streams</Link>
              </Button>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
